'use client';

import { QRCodeCanvas } from 'qrcode.react';

import { useGetUrlWithAnonymousId } from '@flipster/features/src/shared/hooks/useGetUrlWithAnonymousId';
import { FormattedMessage } from '@flipster/i18n';
import { Button } from '@flipster/react/design-system/button';
import { css } from '@flipster/styled-system/css';
import { center, hstack, vstack } from '@flipster/styled-system/patterns';

import { hideIfWebview } from '@web/components/HideIfWebview';

import AppDownloadImage from './app-download-image.webp';

export const AppDownloadSection = () => {
  const getUrlWithAnonymousId = useGetUrlWithAnonymousId();

  return (
    <section
      className={center({
        px: { base: 16, tablet: 40, smallDesktop: 80 },
        h: { base: 'auto', tablet: 560 },
        py: { base: 40, tablet: 0 },
        bgColor: 'background.accent.primary',
      })}
      data-section-type='app-download-cta'
    >
      <div
        className={hstack({
          pos: 'relative',
          maxW: 'desktopMaxWidth',
          w: '100%',
          h: '100%',
          justify: { base: 'center', tablet: 'space-between' },
        })}
      >
        <div
          className={vstack({
            gap: 16,
            alignItems: { base: 'center', tablet: 'start' },
            textAlign: { base: 'center', tablet: 'left' },
          })}
        >
          <h2
            className={css({
              textStyle: 'Display/XL',
              color: 'inverse.white',
              whiteSpace: 'pre-line',
              textWrap: 'balance',
            })}
            data-section-type='footer'
          >
            <FormattedMessage id='Common.r251104.Zero_friction_perps' />
          </h2>
          <p
            className={css({
              textStyle: 'Title/XS',
              color: 'content.default.level-5',
              whiteSpace: 'pre-line',
              textWrap: 'balance',
              mb: 16,
            })}
          >
            <FormattedMessage id='Common.r251104.Discover_new_ways_trade_grow_your_digital_assets' />
          </p>
          <div
            className={hstack({
              maxW: 292,
              gap: 16,
              p: 20,
              rounded: 8,
              bg: 'background.accent.secondary.tint',
              display: { tabletDown: 'none' },
            })}
          >
            <div className={hstack({ gap: 16 })}>
              <QRCodeCanvas
                value={getUrlWithAnonymousId('Download')}
                size={100}
                includeMargin
                className={css({ rounded: 4 })}
              />
              <p
                className={css({
                  textStyle: 'Text/M/Regular',
                  color: 'content.default.level-5',
                  textWrap: 'balance',
                })}
              >
                <FormattedMessage id='Common.r251104.Scan_download_app_for_ios_android' />
              </p>
            </div>
          </div>
          <Button
            hierarchy='primary'
            color='neutral'
            size='large'
            label={<FormattedMessage id='Common.r250901.Download_flipster_app' />}
            css={{ display: { tablet: 'none' }, px: 12 }}
            onClick={() => {
              window.open(getUrlWithAnonymousId('Download'), '_blank', 'noopener');
            }}
          />
        </div>
        <img
          src={AppDownloadImage.src}
          alt='Flipster App Image'
          width={590}
          height={520}
          className={css({ alignSelf: 'flex-end', display: { tabletDown: 'none' } })}
          fetchPriority='low'
        />
      </div>
    </section>
  );
};

export const HideWebviewAppDownloadSection = hideIfWebview(AppDownloadSection);
